/**
 * documentMeta - reading "who uploaded this, and when" off a document row.
 *
 * The same file is described differently by every bucket it can come from: a
 * case file carries `uploaded_at`, an authority document carries `created_at`
 * plus `uploaded_by_user_id`, a streamed citation carries `lastModified`.
 * Both the case-files table and the reader's Document info block ask exactly
 * these two questions, so they ask them through here rather than each keeping
 * its own list of candidate field names.
 *
 * Neither helper invents anything: an empty string means the payload does not
 * say, and the caller renders its own "—".
 */

/** ISO stamp for when the document arrived, or ''. */
export function documentUploadedAt(row: any): string {
  return String(row?.uploaded_at || row?.lastModified || row?.created_at || '');
}

/**
 * The uploader as the payload has it - a NAME where the backend sends one, a
 * bare user id where it does not. Resolve ids with `resolveUploader`.
 */
export function documentUploaderRaw(row: any): string {
  return String(
    row?.uploaded_by_name
      || row?.uploaded_by_user_name
      || row?.uploader_name
      || row?.created_by_name
      || row?.uploaded_by
      || row?.uploaded_by_user_id
      || ''
  );
}

/**
 * A display name for the uploader, or '' when it cannot be known.
 *
 * A numeric id that no roster entry matches resolves to nothing rather than to
 * itself: "Uploaded by 3141" is worse than saying nothing at all.
 */
export function resolveUploader(row: any, namesById?: Map<string, string>): string {
  const raw = documentUploaderRaw(row);
  if (!raw) return '';
  const resolved = namesById?.get(raw);
  if (resolved) return resolved;
  return /^\d+$/.test(raw) ? '' : raw;
}
